/**
 * notifications.js — Web Push client API
 *
 * Manages the browser-side push subscription lifecycle.
 * Directus persistence (save/delete) is delegated to directus.js per the
 * project convention that all API calls live there.
 *
 * Typical enable flow:
 *   1. registerServiceWorker() — registers /sw.js (idempotent)
 *   2. Notification.requestPermission() — asks the user
 *   3. subscribeToPush(reg) — creates PushSubscription in the browser
 *   4. savePushSubscription(sub) [from directus.js] — stores it server-side
 *
 * Typical disable flow:
 *   1. unsubscribeFromPush(reg) — removes browser-side subscription
 *   2. deletePushSubscription(endpoint) [from directus.js] — removes DB record
 *
 * Environment variable required:
 *   REACT_APP_VAPID_PUBLIC_KEY — base64url-encoded VAPID public key
 *   Set in .env.local for dev, Cloudflare Pages environment variables for prod.
 */

/* ── Feature detection ──────────────────────────────────────────────── */

/**
 * True when the browser supports service workers, the Push API, and the
 * Notification API. Safari on iOS 16.4+ qualifies; older browsers do not.
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Returns the current Notification.permission value. */
export function checkPermission() {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

/* ── VAPID public key ───────────────────────────────────────────────── */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function getVapidPublicKey() {
  const key = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!key) throw new Error('REACT_APP_VAPID_PUBLIC_KEY is not set');
  return urlBase64ToUint8Array(key);
}

/* ── Service Worker ─────────────────────────────────────────────────── */

/**
 * Registers /sw.js and resolves with the ServiceWorkerRegistration.
 * Safe to call multiple times — the browser deduplicates registrations.
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) throw new Error('Push notifications not supported in this browser');
  return navigator.serviceWorker.register('/sw.js');
}

/**
 * Returns the active ServiceWorkerRegistration, waiting until it is ready.
 * Assumes registerServiceWorker() was already called on app load (src/index.js).
 */
export async function getServiceWorkerRegistration() {
  if (!isPushSupported()) throw new Error('Push notifications not supported in this browser');
  return navigator.serviceWorker.ready;
}

/* ── Subscription management ────────────────────────────────────────── */

/**
 * Creates a push subscription in the browser using the VAPID public key.
 * If one already exists for this SW, the browser returns the same object.
 *
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<PushSubscription>}
 */
export async function subscribeToPush(registration) {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: getVapidPublicKey(),
  });
}

/**
 * Cancels the push subscription in the browser.
 * Does NOT remove the Directus record — call deletePushSubscription() too.
 *
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<boolean>} true if unsubscribed, false if nothing existed
 */
export async function unsubscribeFromPush(registration) {
  const existing = await registration.pushManager.getSubscription();
  if (!existing) return false;
  return existing.unsubscribe();
}

/**
 * Returns the current PushSubscription for this SW, or null if none.
 *
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<PushSubscription|null>}
 */
export async function getCurrentSubscription(registration) {
  return registration.pushManager.getSubscription();
}
